import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Eraser, Loader2, Check, RotateCcw, AlertTriangle } from 'lucide-react';
import { imageStudioService } from '../../services/imageStudioService';
import { GeneratedImage } from '../../types/imageStudio';
import toast from 'react-hot-toast';

interface BackgroundRemoverProps {
  image: GeneratedImage;
  onComplete: (newImage: GeneratedImage) => void;
}

const BackgroundRemover: React.FC<BackgroundRemoverProps> = ({ image, onComplete }) => {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const resultUrlRef = useRef<string | null>(null);
  useEffect(() => {
    return () => { if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current); };
  }, []);

  const fetchImageBlob = async (): Promise<Blob> => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('token');
    try {
      const resp = await fetch(`${API_URL}/image-studio/${image.id}/proxy?token=${token}`);
      if (!resp.ok) throw new Error(`${resp.status}`);
      const ct = resp.headers.get('content-type') || '';
      if (!ct.startsWith('image/')) throw new Error(ct);
      return await resp.blob();
    } catch {
      const url = image.edited_url || image.original_url;
      if (!url) throw new Error('No image URL');
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`${resp.status}`);
      return await resp.blob();
    }
  };

  const handleRemove = async () => {
    setIsProcessing(true);
    setErrorMsg(null);
    setStatusMessage(t('imageStudio.fetchingImage', 'Fetching image...'));
    try {
      const imgBlob = await fetchImageBlob();
      if (imgBlob.size < 100) throw new Error('Image too small');

      setStatusMessage(t('imageStudio.loadingModel', 'Loading AI model...'));
      const { removeBackground } = await import('@imgly/background-removal');

      setStatusMessage(t('imageStudio.processing', 'Processing...'));
      const result = await removeBackground(imgBlob, {
        device: 'cpu',
        model: 'isnet_quint8',
        proxyToWorker: false,
        output: { format: 'image/png', quality: 1 },
        progress: (key: string, current: number, total: number) => {
          if (total > 0) {
            const pct = Math.round((current / total) * 100);
            setStatusMessage(
              key.includes('fetch') || key.includes('load') || key.includes('Download')
                ? `${t('imageStudio.downloadingModel', 'Downloading model... {{pct}}%', { pct })}`
                : `${t('imageStudio.processingProgress', 'Processing... {{pct}}%', { pct })}`
            );
          }
        }
      });

      if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current);
      const url = URL.createObjectURL(result);
      resultUrlRef.current = url;
      setResultUrl(url);
      setResultBlob(result);
      setStatusMessage('');
      toast.success(t('imageStudio.bgRemovedSuccess', 'Background removed!'));
    } catch (error: any) {
      console.error('Background removal failed:', error);
      setErrorMsg(error?.message || String(error));
      setStatusMessage('');
      toast.error(t('imageStudio.bgRemoveError', 'Failed to remove background.'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUseResult = async () => {
    if (!resultBlob) return;
    setIsProcessing(true);
    try {
      const file = new File([resultBlob], `bg-removed-${Date.now()}.png`, { type: 'image/png' });
      const response = await imageStudioService.saveEdited(image.id, {
        file,
        title: `${image.title || 'Image'} (No Background)`,
        mime_type: 'image/png'
      });
      if (response.success && response.data) onComplete(response.data);
    } catch {
      toast.error(t('imageStudio.saveError', 'Failed to save'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current);
    resultUrlRef.current = null;
    setResultUrl(null);
    setResultBlob(null);
    setErrorMsg(null);
  };

  return (
    <div className="p-3 space-y-3">
      <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
        {t('imageStudio.removeBackground', 'Remove Background')}
      </h3>
      <p className="text-[11px] text-gray-400">
        {t('imageStudio.bgLocalInfo', 'Processed locally in your browser.')}
      </p>

      {/* Compact preview */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <p className="text-[10px] text-gray-400 mb-1">Original</p>
          <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
            <img src={image.edited_url || image.original_url} alt="Original" className="w-full h-full object-contain" />
          </div>
        </div>
        <div>
          <p className="text-[10px] text-gray-400 mb-1">{t('imageStudio.result', 'Result')}</p>
          <div className="aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700"
            style={{ backgroundImage: 'linear-gradient(45deg, #d1d5db 25%, transparent 25%, transparent 75%, #d1d5db 75%), linear-gradient(45deg, #d1d5db 25%, transparent 25%, transparent 75%, #d1d5db 75%)', backgroundSize: '12px 12px', backgroundPosition: '0 0, 6px 6px' }}>
            {resultUrl ? (
              <img src={resultUrl} alt="Result" className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-50/80 dark:bg-gray-800/80">
                <p className="text-[10px] text-gray-400">Preview</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status */}
      {statusMessage && (
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Loader2 className="w-3 h-3 animate-spin shrink-0" />
          <span className="truncate">{statusMessage}</span>
        </div>
      )}

      {/* Error */}
      {errorMsg && (
        <div className="flex items-start gap-1.5 p-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertTriangle className="w-3 h-3 text-red-500 shrink-0 mt-0.5" />
          <p className="text-[11px] text-red-600 dark:text-red-400 break-all">{errorMsg}</p>
        </div>
      )}

      {/* Buttons — compact */}
      {!resultUrl ? (
        <button onClick={handleRemove} disabled={isProcessing}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-medium hover:from-orange-600 hover:to-red-600 disabled:opacity-50 transition-all">
          {isProcessing
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <><Eraser className="w-3.5 h-3.5" /> {errorMsg ? t('imageStudio.retry', 'Retry') : t('imageStudio.removeBg', 'Remove Background')}</>
          }
        </button>
      ) : (
        <div className="flex gap-1.5">
          <button onClick={handleReset} disabled={isProcessing}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
            <RotateCcw className="w-3 h-3" /> {t('imageStudio.retry', 'Retry')}
          </button>
          <button onClick={handleUseResult} disabled={isProcessing}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700 disabled:opacity-50">
            {isProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            {t('imageStudio.useResult', 'Use')}
          </button>
        </div>
      )}
    </div>
  );
};

export default BackgroundRemover;
